import React, { useState, useEffect, useCallback, useMemo } from 'react';
import transfersService from '../services/transfersService';
import { useStore } from '../context/StoreContext';
import {
  FiPlusCircle,
  FiTruck,
  FiCheckCircle,
  FiCornerUpLeft,
  FiAlertTriangle,
  FiSearch,
  FiChevronUp,
  FiChevronDown,
} from 'react-icons/fi';
import TransferFormModal from '../components/inventory/TransferFormModal';
import ReconciliationModal from '../components/inventory/ReconciliationModal';
import TransferDetailsModal from '../components/inventory/TransferDetailsModal';
import { formatInStoreTimezone } from '../utils/dateUtils';
import useStoreTimezone from '../hooks/useStoreTimezone';
import useListView from '../hooks/useListView';

const StockTransferPage = () => {
  const [transfers, setTransfers] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isReconModalOpen, setIsReconModalOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createModalError, setCreateModalError] = useState('');
  const [reconModalError, setReconModalError] = useState('');
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [selectedTransferDetails, setSelectedTransferDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transferStores, setTransferStores] = useState([]);
  const [loadingTransferStores, setLoadingTransferStores] = useState(false);
  const { activeStore, userStores } = useStore();
  const activeStoreId = activeStore?._id;
  const timezone = useStoreTimezone();

  const availableStores = useMemo(() => {
    if (Array.isArray(userStores) && userStores.length > 0) {
      return userStores;
    }
    return activeStore ? [activeStore] : [];
  }, [activeStore, userStores]);

  const {
    filters,
    sort,
    pagination,
    query,
    setFilter,
    setSort,
    setPage,
    setLimit,
    applyServerPagination,
  } = useListView({
    initialFilters: {
      search: '',
      status: '',
      fromStoreId: '',
      toStoreId: '',
      dateFrom: '',
      dateTo: '',
    },
    initialSort: {
      field: 'createdAt',
      direction: 'desc',
    },
    initialPagination: {
      page: 1,
      limit: 25,
    },
  });

  useEffect(() => {
    const normalizedSearch = searchInput.trim();
    const activeSearch = String(filters.search || '').trim();
    if (normalizedSearch === activeSearch) {
      return undefined;
    }
    const timeout = setTimeout(() => {
      setFilter('search', normalizedSearch, { resetPage: true });
    }, 250);
    return () => clearTimeout(timeout);
  }, [searchInput, filters.search, setFilter]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await transfersService.getTransfers(query);
      setTransfers(response?.data || []);
      applyServerPagination(response?.pagination || {});
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load transfers. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [applyServerPagination, query]);

  useEffect(() => {
    if (activeStoreId) {
      fetchData();
    }
  }, [activeStoreId, fetchData]);
  const handleOpenCreateModal = async () => {
    if (!activeStoreId || loadingTransferStores) return;

    try {
      setLoadingTransferStores(true);
      setError('');
      const stores = await transfersService.getTransferStoreOptions();
      setTransferStores(Array.isArray(stores) ? stores : []);
      setCreateModalError('');
      setIsCreateModalOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load stores for this transfer.');
    } finally {
      setLoadingTransferStores(false);
    }
  };


  const handleCreateSave = async (transferData) => {
    try {
      await transfersService.createTransfer(transferData);
      fetchData();
      setIsCreateModalOpen(false);
      setCreateModalError('');
    } catch (err) {
      setCreateModalError(err.response?.data?.message || 'Failed to create transfer.');
    }
  };

  const handleUpdateStatus = async (transferId, status) => {
    const confirmText = status === 'Shipped' 
      ? 'This will deduct stock from your store. Are you sure?'
      : 'This will add the rejected stock back to your inventory. Are you sure?';
    if (window.confirm(confirmText)) {
      try {
        await transfersService.updateTransferStatus(transferId, status);
        fetchData();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to update status.');
      }
    }
  };

  const handleOpenReconModal = (transfer) => {
    setSelectedTransfer(transfer);
    setReconModalError('');
    setIsReconModalOpen(true);
  };

  const handleSaveReconciliation = async (reconData) => {
    try {
      await transfersService.receiveTransfer(selectedTransfer._id, reconData);
      fetchData();
      setIsReconModalOpen(false);
      setSelectedTransfer(null);
      setReconModalError('');
    } catch (err) {
      setReconModalError(err.response?.data?.message || 'Failed to process receipt.');
    }
  };

  const handleOpenDetailsModal = async (transferId) => {
    setIsDetailsModalOpen(true);
    setDetailsLoading(true);
    setDetailsError('');
    setSelectedTransferDetails(null);
    try {
      const transfer = await transfersService.getTransferById(transferId);
      setSelectedTransferDetails(transfer);
    } catch (err) {
      setDetailsError(err.response?.data?.message || 'Failed to load transfer details.');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleReconcile = async (transferId) => {
    if (window.confirm('This will adjust your stock levels to account for the discrepancy. This action cannot be undone. Are you sure?')) {
        try {
            const res = await transfersService.reconcileDiscrepancy(transferId);
            setSuccess(res.message);
            setTimeout(() => setSuccess(''), 4000);
            fetchData();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to reconcile.');
        }
    }
  };

  const sortIcon = useCallback((field) => {
    if (sort.field !== field) return null;
    return sort.direction === 'asc' ? <FiChevronUp className="inline ml-1" /> : <FiChevronDown className="inline ml-1" />;
  }, [sort.direction, sort.field]);

  const totalCount = Number(pagination?.total) || 0;
  const currentPage = Number(pagination?.page) || 1;
  const totalPages = Number(pagination?.pages) || 1;
  const pageSize = Number(pagination?.limit) || 25;
  const visibleStart = transfers.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0;
  const visibleEnd = transfers.length > 0 ? visibleStart + transfers.length - 1 : 0;
  const pageSizeOptions = [25, 50, 100];

  const getStatusChip = (status) => {
    const styles = {
      Pending: 'bg-yellow-100 text-yellow-800',
      Shipped: 'bg-blue-100 text-blue-800',
      Completed: 'bg-green-100 text-green-800',
      'Completed with Discrepancy': 'bg-teal-100 text-teal-800',
      Cancelled: 'bg-gray-100 text-gray-800',
      Rejected: 'bg-red-100 text-red-800',
      Returned: 'bg-purple-100 text-purple-800',
      Reconciled: 'bg-gray-500 text-white',
    };
    return <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${styles[status] || styles.Cancelled}`}>{status}</span>;
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Stock Transfers</h1>
        <button
          onClick={handleOpenCreateModal}
          className="flex items-center bg-primary text-white px-4 py-2 rounded-md shadow hover:bg-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed"
          disabled={!activeStore?._id || loadingTransferStores}
        >
          <FiPlusCircle className="mr-2" /> {loadingTransferStores ? 'Loading stores...' : 'New Transfer'}
        </button>
      </div>
      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      {success && <p className="bg-green-100 text-green-700 p-3 rounded mb-4">{success}</p>}

      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by transfer ID or notes..."
              className="pl-10 pr-4 py-2 border rounded-md w-full"
            />
          </div>
          <div>
            <select
              value={filters.status || ''}
              onChange={(e) => setFilter('status', e.target.value)}
              className="border rounded-md w-full py-2 px-3"
            >
              <option value="">All statuses</option>
              <option value="Pending">Pending</option>
              <option value="Shipped">Shipped</option>
              <option value="Completed">Completed</option>
              <option value="Completed with Discrepancy">Completed with Discrepancy</option>
              <option value="Reconciled">Reconciled</option>
              <option value="Rejected">Rejected</option>
              <option value="Returned">Returned</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <select
              value={filters.fromStoreId || ''}
              onChange={(e) => setFilter('fromStoreId', e.target.value)}
              className="border rounded-md w-full py-2 px-3"
            >
              <option value="">All from stores</option>
              {availableStores.map((store) => (
                <option key={store._id} value={store._id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={filters.toStoreId || ''}
              onChange={(e) => setFilter('toStoreId', e.target.value)}
              className="border rounded-md w-full py-2 px-3"
            >
              <option value="">All to stores</option>
              {availableStores.map((store) => (
                <option key={store._id} value={store._id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <input
              type="date"
              value={filters.dateFrom || ''}
              onChange={(e) => setFilter('dateFrom', e.target.value)}
              className="border rounded-md w-full py-2 px-3"
              aria-label="From date"
            />
          </div>
          <div>
            <input
              type="date"
              value={filters.dateTo || ''}
              onChange={(e) => setFilter('dateTo', e.target.value)}
              className="border rounded-md w-full py-2 px-3"
              aria-label="To date"
            />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <label className="text-sm text-gray-500">Rows</label>
          <select
            value={pageSize}
            onChange={(e) => setLimit(e.target.value)}
            className="border rounded-md py-2 px-3 text-sm"
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <button type="button" onClick={() => setSort('createdAt')} className="hover:text-gray-700">
                  Date{sortIcon('createdAt')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button type="button" onClick={() => setSort('transferId')} className="hover:text-gray-700">
                  ID{sortIcon('transferId')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">From</th>
              <th className="px-4 py-3 text-left">To</th>
              <th className="px-4 py-3 text-left">
                <button type="button" onClick={() => setSort('status')} className="hover:text-gray-700">
                  Status{sortIcon('status')}
                </button>
              </th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                  Loading transfers...
                </td>
              </tr>
            )}
            {!loading && transfers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                  No transfers found.
                </td>
              </tr>
            )}
            {transfers.map(t => (
              <tr key={t._id}>
                <td className="px-4 py-4 text-sm">
                  {formatInStoreTimezone(t.createdAt, 'dd-MM-yyyy', timezone)}
                </td>
                <td className="px-4 py-4 font-mono text-sm">
                  <button
                    type="button"
                    onClick={() => handleOpenDetailsModal(t._id)}
                    className="text-primary hover:text-primary-dark underline underline-offset-2"
                    title="View transfer details"
                  >
                    {t.transferId}
                  </button>
                </td>
                <td className="px-4 py-4">{t.fromStore.name}</td>
                <td className="px-4 py-4">{t.toStore.name}</td>
                <td className="px-4 py-4">{getStatusChip(t.status)}</td>
                <td className="px-4 py-4 text-center">
                  {t.status === 'Pending' && activeStore?._id === t.fromStore._id && (
                    <button onClick={() => handleUpdateStatus(t._id, 'Shipped')} className="text-blue-600 hover:text-blue-900 flex items-center mx-auto" title="Mark as Shipped"><FiTruck className="mr-1"/> Ship</button>
                  )}
                  {t.status === 'Shipped' && activeStore?._id === t.toStore._id && (
                    <button onClick={() => handleOpenReconModal(t)} className="text-accent-green hover:text-accent-green-dark flex items-center mx-auto" title="Receive Stock"><FiCheckCircle className="mr-1"/> Receive</button>
                  )}
                  {t.status === 'Rejected' && activeStore?._id === t.fromStore._id && (
                    <button onClick={() => handleUpdateStatus(t._id, 'Returned')} className="text-purple-600 hover:text-purple-900 flex items-center mx-auto" title="Re-Receive Rejected Stock"><FiCornerUpLeft className="mr-1"/> Re-Receive</button>
                  )}
                  {t.status === 'Completed with Discrepancy' && activeStore?._id === t.fromStore._id && (
                    <button onClick={() => handleReconcile(t._id)} className="text-secondary hover:text-secondary-dark flex items-center mx-auto" title="Reconcile Discrepancy"><FiAlertTriangle className="mr-1"/> Reconcile</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-600">
        <div>
          {totalCount > 0
            ? `Showing ${visibleStart}-${visibleEnd} of ${totalCount} transfers`
            : 'No transfers to display'}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage(currentPage - 1)}
            disabled={loading || currentPage <= 1}
            className="px-3 py-1.5 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span>Page {currentPage} of {Math.max(totalPages, 1)}</span>
          <button
            type="button"
            onClick={() => setPage(currentPage + 1)}
            disabled={loading || currentPage >= totalPages}
            className="px-3 py-1.5 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
      {isCreateModalOpen && (
        <TransferFormModal
          stores={transferStores}
          products={[]}
          activeStoreId={activeStoreId}
          error={createModalError}
          onSave={handleCreateSave}
          onCancel={() => {
            setIsCreateModalOpen(false);
            setCreateModalError('');
          }}
        />
      )}
      {isReconModalOpen && selectedTransfer && (
        <ReconciliationModal
          transfer={selectedTransfer}
          error={reconModalError}
          onSave={handleSaveReconciliation}
          onCancel={() => {
            setIsReconModalOpen(false);
            setSelectedTransfer(null);
            setReconModalError('');
          }}
        />
      )}
      {isDetailsModalOpen && (
        <TransferDetailsModal
          transfer={selectedTransferDetails}
          loading={detailsLoading}
          error={detailsError}
          timezone={timezone}
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedTransferDetails(null);
            setDetailsError('');
            setDetailsLoading(false);
          }}
        />
      )}
    </div>
  );
};

export default StockTransferPage;
